import React, { Suspense, lazy } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';

// ============================================
// CRITICAL COMPONENTS - NOT LAZY LOADED
// These are above-the-fold and SEO-critical
// ============================================
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Locations from './components/Locations';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import FloatingButtons from './components/FloatingButtons';
import { PageLoader, ComponentLoader } from './components/LazyLoadWrapper';

// Auth Providers (needed immediately)
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import { AgentAuthProvider } from './contexts/AgentAuthContext';
import PrivateRoute from './components/PrivateRoute';
import AgentProtectedRoute from './components/AgentProtectedRoute';

// ============================================
// LAZY LOADED HOMEPAGE COMPONENTS
// Heavy sections loaded after initial render
// ============================================
const Excellence = lazy(() => import('./components/Excellence'));
const SafetyStandards = lazy(() => import('./components/SafetyStandards'));
const MedicalRequirements = lazy(() => import('./components/MedicalRequirements'));
const JumpDiaries = lazy(() => import('./components/JumpDiaries'));
const Franchise = lazy(() => import('./components/Franchise'));
const Gallery = lazy(() => import('./components/Gallery'));
const Testimonials = lazy(() => import('./components/Testimonials'));
const Contact = lazy(() => import('./components/Contact'));
const MediaShowcase = lazy(() => import('./components/MediaShowcase'));
const StatsStrip = lazy(() => import('./components/StatsStrip'));
const PromoBanner = lazy(() => import('./components/PromoBanner'));
const InclinedBungyPromo = lazy(() => import('./components/InclinedBungyPromo'));

// ============================================
// LAZY LOADED PAGES - Route-level code splitting
// ============================================
const LocationsPage = lazy(() => import('./pages/LocationsPage'));
const LocationDetailPage = lazy(() => import('./pages/LocationDetailPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const BookingSuccessPage = lazy(() => import('./pages/BookingSuccessPage'));
const BookingFailurePage = lazy(() => import('./pages/BookingFailurePage'));
const RefundPage = lazy(() => import('./pages/RefundPage'));
const CancellationPage = lazy(() => import('./pages/CancellationPage'));
const SafetyPage = lazy(() => import('./pages/SafetyPage'));
const BlogsPage = lazy(() => import('./pages/BlogsPage'));
const BlogDetailPage = lazy(() => import('./pages/BlogDetailPage'));
const FranchisePage = lazy(() => import('./pages/FranchisePage'));
const MOTPage = lazy(() => import('./pages/MOTPage'));
const SANZPage = lazy(() => import('./pages/SANZPage'));
const ATOAIPage = lazy(() => import('./pages/ATOAIPage'));
const GoaTourismPage = lazy(() => import('./pages/GoaTourismPage'));
const SafetyStandardsPage = lazy(() => import('./pages/SafetyStandardsPage'));
const EquipmentsPage = lazy(() => import('./pages/EquipmentsPage'));
const WebCheckinPage = lazy(() => import('./pages/WebCheckinPage'));
const WaiverFormPage = lazy(() => import('./pages/WaiverFormPage'));

// ============================================
// LAZY LOADED ADMIN COMPONENTS
// ============================================
const AdminLogin = lazy(() => import('./admin/AdminLogin'));
const AdminLayout = lazy(() => import('./admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./admin/AdminDashboard'));
const BlogsManager = lazy(() => import('./admin/BlogsManager'));
const CouponsManager = lazy(() => import('./admin/CouponsManager'));
const MediaManager = lazy(() => import('./admin/MediaManager'));
const LocationsManager = lazy(() => import('./admin/LocationsManager'));
const CertificatesManager = lazy(() => import('./admin/CertificatesManager'));
const ExcellenceManager = lazy(() => import('./admin/ExcellenceManager'));
const TestimonialsManager = lazy(() => import('./admin/TestimonialsManager'));
const BookingsManager = lazy(() => import('./admin/BookingsManager'));
const AgenciesWrapper = lazy(() => import('./admin/AgenciesWrapper'));
const AgencyDetails = lazy(() => import('./admin/AgencyDetails'));
const OffersManager = lazy(() => import('./admin/OffersManager'));
const ReceptionManager = lazy(() => import('./admin/ReceptionManager'));
const BungyManManager = lazy(() => import('./admin/BungyManManager'));
const SiteManagement = lazy(() => import('./admin/SiteManagement/SiteManagementDashboard'));
const PaymentDetailsPage = lazy(() => import('./admin/PaymentDetailsPage'));
const LinkGenerator = lazy(() => import('./admin/LinkGenerator'));
const SettingsManager = lazy(() => import('./admin/SettingsManager'));

// ============================================
// STAFF PORTAL COMPONENT
// ============================================
const StaffDashboard = lazy(() => import('./staff/StaffDashboard'));

// ============================================
// LAZY LOADED AGENT PORTAL COMPONENTS
// ============================================
const AgentLogin = lazy(() => import('./agent/AgentLogin'));
const AgentRegister = lazy(() => import('./agent/AgentRegister'));
const AgentDashboard = lazy(() => import('./agent/AgentDashboard'));
const AgentBookingForm = lazy(() => import('./agent/AgentBookingForm'));
const AgentSlotsViewer = lazy(() => import('./agent/AgentSlotsViewer'));
const AgentMessageSender = lazy(() => import('./agent/AgentMessageSender'));
const AgentOffers = lazy(() => import('./agent/AgentOffers'));
const AgentTransactions = lazy(() => import('./agent/AgentTransactions'));
const AgentNotifications = lazy(() => import('./agent/AgentNotifications'));
const AgentPayNow = lazy(() => import('./agent/AgentPayNow'));
const AgentAllBookings = lazy(() => import('./agent/AgentAllBookings'));

// ============================================
// HOMEPAGE COMPONENT
// Critical content (Hero, Locations) loads immediately
// Heavy sections load progressively
// ============================================
const HomePage = () => {
  React.useEffect(() => {
    // Force scroll to top immediately
    window.scrollTo(0, 0);
    
    // Remove any hash from URL (for search engine results)
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname);
    }
    
    // Additional safety check after a short delay
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-black">
      {/* Critical above-the-fold content - NOT lazy loaded */}
      <Hero />
      <Locations />
      
      {/* Below-the-fold content - Lazy loaded for performance */}
      <Suspense fallback={<ComponentLoader height="h-32" />}>
        <StatsStrip />
      </Suspense>
      
      <Suspense fallback={<ComponentLoader height="h-96" />}>
        <SafetyStandards />
      </Suspense>
      
      <Suspense fallback={<ComponentLoader height="h-screen" />}>
        <MediaShowcase />
      </Suspense>
      
      <Suspense fallback={<ComponentLoader height="h-96" />}>
        <MedicalRequirements />
      </Suspense>
      
      <Suspense fallback={<ComponentLoader height="h-64" />}>
        <InclinedBungyPromo />
      </Suspense>
      
      <Suspense fallback={<ComponentLoader height="h-96" />}>
        <Excellence />
      </Suspense>
      
      <Suspense fallback={<ComponentLoader height="h-96" />}>
        <Franchise />
      </Suspense>
      
      <Suspense fallback={<ComponentLoader height="h-screen" />}>
        <Testimonials />
      </Suspense>
      
      <Suspense fallback={<ComponentLoader height="h-96" />}>
        <Contact />
      </Suspense>
    </div>
  );
};

// ============================================
// MAIN APP COMPONENT
// ============================================
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AdminAuthProvider>
          <AgentAuthProvider>
            <Routes>
              {/* Admin Routes - No Navbar/Footer */}
              <Route path="/admin" element={
                <Suspense fallback={<PageLoader />}>
                  <AdminLogin />
                </Suspense>
              } />
              <Route path="/admin/login" element={
                  <Suspense fallback={<PageLoader />}>
                    <AdminLogin />
                  </Suspense>
                } />
                <Route path="/admin/*" element={
                  <PrivateRoute>
                    <Suspense fallback={<PageLoader />}>
                    <AdminLayout />
                  </Suspense>
                </PrivateRoute>
              }>
                <Route path="dashboard" element={
                  <Suspense fallback={<PageLoader />}>
                    <AdminDashboard />
                  </Suspense>
                } />
                <Route path="media" element={
                  <Suspense fallback={<PageLoader />}>
                    <MediaManager />
                  </Suspense>
                } />
                <Route path="locations" element={
                  <Suspense fallback={<PageLoader />}>
                    <LocationsManager />
                  </Suspense>
                } />
                <Route path="certificates" element={
                  <Suspense fallback={<PageLoader />}>
                    <CertificatesManager />
                  </Suspense>
                } />
                <Route path="bookings" element={
                  <Suspense fallback={<PageLoader />}>
                    <BookingsManager />
                  </Suspense>
                } />
                <Route path="bookings/:bookingId/payment" element={
                  <Suspense fallback={<PageLoader />}>
                    <PaymentDetailsPage />
                  </Suspense>
                } />
                <Route path="site-management" element={
                  <Suspense fallback={<PageLoader />}>
                    <SiteManagement />
                  </Suspense>
                } />
                <Route path="agencies" element={
                  <Suspense fallback={<PageLoader />}>
                    <AgenciesWrapper />
                  </Suspense>
                } />
                <Route path="agencies/:id" element={
                  <Suspense fallback={<PageLoader />}>
                    <AgencyDetails />
                  </Suspense>
                } />
                <Route path="offers" element={
                  <Suspense fallback={<PageLoader />}>
                    <OffersManager />
                  </Suspense>
                } />
                <Route path="testimonials" element={
                  <Suspense fallback={<PageLoader />}>
                    <TestimonialsManager />
                  </Suspense>
                } />
                <Route path="excellence" element={
                  <Suspense fallback={<PageLoader />}>
                    <ExcellenceManager />
                  </Suspense>
                } />
                <Route path="coupons" element={
                  <Suspense fallback={<PageLoader />}>
                    <CouponsManager />
                  </Suspense>
                } />
                <Route path="blogs" element={
                  <Suspense fallback={<PageLoader />}>
                    <BlogsManager />
                  </Suspense>
                } />
                <Route path="reception" element={
                  <Suspense fallback={<PageLoader />}>
                    <ReceptionManager />
                  </Suspense>
                } />
                <Route path="bungyman" element={
                  <Suspense fallback={<PageLoader />}>
                    <BungyManManager />
                  </Suspense>
                } />
                <Route path="link-generator" element={
                  <Suspense fallback={<PageLoader />}>
                    <LinkGenerator />
                  </Suspense>
                } />
                <Route path="settings" element={
                  <Suspense fallback={<PageLoader />}>
                    <SettingsManager />
                  </Suspense>
                } />
                <Route path="contacts" element={<div className="text-white">Contacts Manager Coming Soon</div>} />
              </Route>

              {/* Staff Routes - No Navbar/Footer */}
              <Route path="/staff/dashboard" element={
                <Suspense fallback={<PageLoader />}>
                  <StaffDashboard />
                </Suspense>
              } />

              {/* Agent Routes - No Navbar/Footer */}
              <Route path="/agent/login" element={
                <Suspense fallback={<PageLoader />}>
                  <AgentLogin />
                </Suspense>
              } />
              <Route path="/agent/register" element={
                <Suspense fallback={<PageLoader />}>
                  <AgentRegister />
                </Suspense>
              } />
              <Route path="/agent/dashboard" element={
                <AgentProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <AgentDashboard />
                  </Suspense>
                </AgentProtectedRoute>
              } />
              <Route path="/agent/booking/create" element={
                <AgentProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <AgentBookingForm />
                  </Suspense>
                </AgentProtectedRoute>
              } />
              <Route path="/agent/slots" element={
                <AgentProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <AgentSlotsViewer />
                  </Suspense>
                </AgentProtectedRoute>
              } />
              <Route path="/agent/send-message" element={
                <AgentProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <AgentMessageSender />
                  </Suspense>
                </AgentProtectedRoute>
              } />
              <Route path="/agent/offers" element={
                <AgentProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <AgentOffers />
                  </Suspense>
                </AgentProtectedRoute>
              } />
              <Route path="/agent/transactions" element={
                <AgentProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <AgentTransactions />
                  </Suspense>
                </AgentProtectedRoute>
              } />
              <Route path="/agent/notifications" element={
                <AgentProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <AgentNotifications />
                  </Suspense>
                </AgentProtectedRoute>
              } />
              <Route path="/agent/pay-now" element={
                <AgentProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <AgentPayNow />
                  </Suspense>
                </AgentProtectedRoute>
              } />
              <Route path="/agent/bookings" element={
                <AgentProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <AgentAllBookings />
                  </Suspense>
                </AgentProtectedRoute>
              } />

              {/* Public Routes - With Navbar/Footer */}
              <Route path="*" element={
                <>
                  <Navbar />
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    
                    <Route path="/about" element={
                      <div className="pt-20">
                        <Suspense fallback={<PageLoader />}>
                          <AboutPage />
                        </Suspense>
                      </div>
                    } />
                    
                    <Route path="/about-us" element={
                      <div className="pt-20">
                        <Suspense fallback={<PageLoader />}>
                          <AboutPage />
                        </Suspense>
                      </div>
                    } />
                    
                    <Route path="/locations" element={
                      <div className="pt-20">
                        <Suspense fallback={<PageLoader />}>
                          <LocationsPage />
                        </Suspense>
                      </div>
                    } />
                    
                    <Route path="/location/:locationName" element={
                      <Suspense fallback={<PageLoader />}>
                        <LocationDetailPage />
                      </Suspense>
                    } />
                    
                    <Route path="/book" element={
                      <div className="pt-20 min-h-screen bg-black flex items-center justify-center">
                        <h1 className="text-white text-4xl font-bold">Booking Page Coming Soon</h1>
                      </div>
                    } />
                    
                    <Route path="/gallery" element={
                      <div className="pt-20 bg-black">
                        <Suspense fallback={<PageLoader />}>
                          <Gallery />
                        </Suspense>
                      </div>
                    } />
                    
                    <Route path="/franchise" element={
                      <div className="pt-20">
                        <Suspense fallback={<PageLoader />}>
                          <FranchisePage />
                        </Suspense>
                      </div>
                    } />
                    
                    <Route path="/contact" element={
                      <div className="pt-20 bg-black">
                        <Suspense fallback={<PageLoader />}>
                          <Contact />
                        </Suspense>
                      </div>
                    } />
                    
                    <Route path="/terms" element={
                      <div className="pt-20">
                        <Suspense fallback={<PageLoader />}>
                          <TermsPage />
                        </Suspense>
                      </div>
                    } />
                    
                    <Route path="/privacy" element={
                      <div className="pt-20">
                        <Suspense fallback={<PageLoader />}>
                          <PrivacyPage />
                        </Suspense>
                      </div>
                    } />
                    
                    <Route path="/booking-success" element={
                      <div className="pt-20">
                        <Suspense fallback={<PageLoader />}>
                          <BookingSuccessPage />
                        </Suspense>
                      </div>
                    } />
                    
                    <Route path="/booking-failure" element={
                      <div className="pt-20">
                        <Suspense fallback={<PageLoader />}>
                          <BookingFailurePage />
                        </Suspense>
                      </div>
                    } />
                    
                    <Route path="/refund" element={
                      <div className="pt-20">
                        <Suspense fallback={<PageLoader />}>
                          <RefundPage />
                        </Suspense>
                      </div>
                    } />
                    
                    <Route path="/cancellation" element={
                      <div className="pt-20">
                        <Suspense fallback={<PageLoader />}>
                          <CancellationPage />
                        </Suspense>
                      </div>
                    } />
                    
                    <Route path="/safety" element={
                      <div className="pt-20">
                        <Suspense fallback={<PageLoader />}>
                          <SafetyPage />
                        </Suspense>
                      </div>
                    } />
                    
                    <Route path="/blogs" element={
                      <div className="pt-20 min-h-screen">
                        <Suspense fallback={<PageLoader />}>
                          <BlogsPage />
                        </Suspense>
                      </div>
                    } />
                    
                    <Route path="/blog/:slug" element={
                      <div className="pt-20 min-h-screen">
                        <Suspense fallback={<PageLoader />}>
                          <BlogDetailPage />
                        </Suspense>
                      </div>
                    } />
                    
                    <Route path="/mot" element={
                      <div className="pt-20">
                        <Suspense fallback={<PageLoader />}>
                          <MOTPage />
                        </Suspense>
                      </div>
                    } />
                    
                    <Route path="/sanz" element={
                      <div className="pt-20">
                        <Suspense fallback={<PageLoader />}>
                          <SANZPage />
                        </Suspense>
                      </div>
                    } />
                    
                    <Route path="/atoai" element={
                      <div className="pt-20">
                        <Suspense fallback={<PageLoader />}>
                          <ATOAIPage />
                        </Suspense>
                      </div>
                    } />
                    
                    <Route path="/goa-tourism" element={
                      <div className="pt-20">
                        <Suspense fallback={<PageLoader />}>
                          <GoaTourismPage />
                        </Suspense>
                      </div>
                    } />
                    
                    <Route path="/safety-standards" element={
                      <div className="pt-20">
                        <Suspense fallback={<PageLoader />}>
                          <SafetyStandardsPage />
                        </Suspense>
                      </div>
                    } />
                    
                    <Route path="/equipments" element={
                      <div className="pt-20">
                        <Suspense fallback={<PageLoader />}>
                          <EquipmentsPage />
                        </Suspense>
                      </div>
                    } />
                    
                    <Route path="/checkin" element={
                      <Suspense fallback={<PageLoader />}>
                        <WebCheckinPage />
                      </Suspense>
                    } />
                    
                    <Route path="/checkin/:token" element={
                      <Suspense fallback={<PageLoader />}>
                        <WebCheckinPage />
                      </Suspense>
                    } />
                    
                    <Route path="/web-checkin" element={
                      <Suspense fallback={<PageLoader />}>
                        <WaiverFormPage />
                      </Suspense>
                    } />
                    
                    <Route path="/web-checkin/:bookingId" element={
                      <Suspense fallback={<PageLoader />}>
                        <WaiverFormPage />
                      </Suspense>
                    } />
                    
                    <Route path="/coming-soon" element={
                      <div className="pt-20 min-h-screen bg-black flex items-center justify-center">
                        <h1 className="text-white text-4xl font-bold">Coming Soon</h1>
                      </div>
                    } />
                  </Routes>
                  <Footer />
                  <FloatingButtons />
                </>
              } />
            </Routes>
          </AgentAuthProvider>
        </AdminAuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
